import type { Table } from '@tanstack/react-table';
import { Check, ChevronDown } from 'lucide-react';

import { Translation } from '@/hoslog-intl';
import { cn } from '@/lib/utils';
import * as React from 'react';
import { Badge } from 'ui/badge';
import { Button } from 'ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from 'ui/command';
import { Popover, PopoverContent, PopoverTrigger } from 'ui/popover';

interface DataTableViewOptionsProps<TData> {
  table: Table<TData>;
}

export function DataTableViewOptions<TData>({ table }: DataTableViewOptionsProps<TData>) {
  const columns = React.useMemo(
    () => table.getAllColumns().filter((column) => typeof column.accessorFn !== 'undefined' && column.getCanHide()),
    [table],
  );
  const hiddenColumnCount = columns.filter((column) => !column.getIsVisible()).length;

  return (
    <Popover>
      <PopoverTrigger className="px-1.5" asChild>
        <Button
          aria-label="Toggle columns"
          role="combobox"
          variant="filter"
          size="sm"
          className="ml-auto hidden h-8 lg:flex"
        >
          <Translation id="common.page.button.hideColumns" />{' '}
          <Badge className="bg-foreground-subtle">{hiddenColumnCount}</Badge>
          <ChevronDown className="ml-auto" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-44 p-0">
        <Command>
          <CommandInput placeholder="Search columns..." />
          <CommandList>
            <CommandEmpty>No columns found.</CommandEmpty>
            <CommandGroup>
              {columns.map((column) => (
                <CommandItem key={column.id} onSelect={() => column.toggleVisibility(!column.getIsVisible())}>
                  <span className="truncate">
                    {column.columnDef.meta?.hideColumnLabelKey ? (
                      <Translation id={column.columnDef.meta.hideColumnLabelKey} />
                    ) : (
                      (column.columnDef.meta?.label ?? column.id)
                    )}
                  </span>

                  <Check
                    className={cn('ml-auto size-4 shrink-0', column.getIsVisible() ? 'opacity-100' : 'opacity-0')}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
